import { EventBus }       from '../core/EventBus.js';
import { GameState }      from '../core/GameState.js';
import { formatDuration } from './formatting.js';

/**
 * Port-time helpers: cargo handling (TradeSystem, WarehouseSystem, contract
 * systems), bunkering (FuelSystem) and repairs. The timing maths lives on
 * Ship (addHandling / addBunkering / addRepair); this applies it and announces the change so
 * the Fleet panel and the multiplayer relay pick it up.
 */

/** Add handling time for `teu` TEU moved at `port`. Returns the game hours added. */
export function beginCargoHandling(ship, port, teu, op) {
  const hours = ship.addHandling(port, teu, op);
  if (hours > 0) EventBus.emit('ship:handlingChanged', { ship });
  return hours;
}

/** Add bunkering time for `tonnes` of fuel at `port` (parallel to cargo handling). */
export function beginBunkering(ship, port, tonnes) {
  const hours = ship.addBunkering(port, tonnes);
  if (hours > 0) EventBus.emit('ship:handlingChanged', { ship });
  return hours;
}

/** Add repair time for `points` of condition at `port` (parallel to cargo handling and bunkering). */
export function beginRepair(ship, port, points) {
  const hours = ship.addRepair(port, points);
  if (hours > 0) EventBus.emit('ship:handlingChanged', { ship });
  return hours;
}

/** "45 min", "6 hrs", "2 days 3 hrs" */
export function formatHandlingTime(hours) {
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} min`;
  return formatDuration(hours / 24);
}

/**
 * Everything holding a ship in port, e.g. "Loading 3 hrs · Refuelling 1 hr,
 * then sails for Rotterdam" or "Detained — repair to 50%". '' when free.
 */
export function describeHandling(ship) {
  if (!ship?.getBusyHoursRemaining) return '';
  const now = GameState.currentDate;
  const parts = [];

  const dock = ship.maintenance;
  if (ship.status === 'drydock' && dock?.drydockUntil) {
    const hrs = Math.max(0, (new Date(dock.drydockUntil) - now) / 3600000);
    parts.push(`In dry dock — ${formatHandlingTime(hrs)} left`);
  }
  if (dock?.detention) {
    parts.push(dock.detention.reason === 'drydock'
      ? 'Detained — dry dock overdue'
      : `Detained — repair to ${dock.detention.minCondition}%`);
  }
  const cargo = ship.getHandlingHoursRemaining(now);
  if (cargo > 0) parts.push(`${ship.handlingOp === 'unloading' ? 'Unloading' : 'Loading'} ${formatHandlingTime(cargo)}`);
  const fuel = ship.getBunkeringHoursRemaining(now);
  if (fuel > 0) parts.push(`Refuelling ${formatHandlingTime(fuel)}`);
  const repair = ship.getRepairHoursRemaining(now);
  if (repair > 0) parts.push(`Repairing ${formatHandlingTime(repair)}`);
  if (parts.length === 0) return '';

  let text = parts.join(' · ');
  const dest = ship.pendingDeparture ? GameState.getPort(ship.pendingDeparture.portId) : null;
  if (dest) text += `, then sails for ${dest.name}`;
  return text;
}
